/**
 * Pure display helpers for the admin Users view: turning a user's limit
 * state + usage into a compact, legible label, and formatting LLM cost in
 * dollars. Kept pure (no React) so they're trivially testable.
 */

import type { LimitState } from "./adminApi";

export type LimitTone = "muted" | "ok" | "near" | "over";

export interface LimitCell {
  /** Short text for the table cell, e.g. "120 / 300" or "Walled". */
  label: string;
  /** A one-word qualitative state used to tint the cell. */
  tone: LimitTone;
}

/**
 * Compact limit summary for a user row. `metered` tiers show used/cap with a
 * tone that escalates as they approach the cap (80% = near, at-cap = over);
 * the free hard wall reads "Walled"; pre-report + unlimited are uncapped.
 */
export function limitCell(args: {
  limitState: LimitState;
  monthlyCap: number | null;
  responsesThisMonth: number;
}): LimitCell {
  const { limitState, monthlyCap, responsesThisMonth } = args;
  switch (limitState) {
    case "pre_report":
      return { label: "Pre-report", tone: "muted" };
    case "walled":
      return { label: "Walled", tone: "over" };
    case "uncapped":
      return { label: "Unlimited", tone: "muted" };
    case "metered": {
      if (monthlyCap == null) return { label: "Metered", tone: "muted" };
      const frac = monthlyCap > 0 ? responsesThisMonth / monthlyCap : 0;
      const tone: LimitTone =
        responsesThisMonth >= monthlyCap ? "over" : frac >= 0.8 ? "near" : "ok";
      return { label: `${responsesThisMonth} / ${monthlyCap}`, tone };
    }
  }
}

/** One-line human description of the limit kind, for the detail panel. */
export function limitStateLabel(state: LimitState): string {
  switch (state) {
    case "pre_report":
      return "Free · uncapped until first leak report";
    case "walled":
      return "Free · hard wall (first report reached)";
    case "metered":
      return "Metered monthly cap";
    case "uncapped":
      return "Uncapped (unlimited tier)";
  }
}

/**
 * Dollars with adaptive precision: cents for amounts ≥ $1 (e.g. "$6.26"),
 * 4 decimals for the small per-turn figures (e.g. "$0.0673") so they don't
 * collapse to "$0.00".
 */
export function formatUsd(n: number): string {
  if (n === 0) return "$0";
  const decimals = Math.abs(n) >= 1 ? 2 : 4;
  return `$${n.toFixed(decimals)}`;
}

/** Compact token counts: "6.3M", "118K", "942". */
export function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(n);
}
